import React from 'react';
import { ButtonGroup, Button } from '@mui/material';
import { buildClassName } from 'utils/build-class-name';

//#region Types
export interface ToggleButtonGroupOption<T extends string | number> {
  value: T;
  label: string;
  disabled?: boolean;
}

export type ToggleButtonGroupSize = 'small' | 'medium' | 'large';

export interface ToggleButtonGroupProps<T extends string | number> {
  /** Available options; exactly one can be selected at a time */
  options: ToggleButtonGroupOption<T>[];
  /** Currently selected value */
  value: T;
  /** Handler invoked with the value of the option that was clicked */
  onValueChange: (value: T) => void;
  /** Size variant */
  size?: ToggleButtonGroupSize;
  /** Stretch the group and its buttons to fill the available width */
  fullWidth?: boolean;
  /** Aria label describing the group for assistive technology */
  ariaLabel?: string;
  /** Disable the entire group */
  disabled?: boolean;
  /** Additional CSS class applied to the group */
  className?: string;
  /** Test ID for automated testing */
  testId?: string;
}
//#endregion

/**
 * ToggleButtonGroup - A segmented, single-select group of buttons.
 *
 * Atomic Level: Molecule
 *
 * A set of two or more buttons where exactly one option is selected at a time.
 * Clicking an option selects it and calls `onValueChange` with that option's value.
 *
 * Features:
 * - Single-select segmented control (one active option at a time)
 * - Generic over string or number values
 * - Multiple sizes: small, medium, large
 * - Optional full-width layout
 * - Per-option and whole-group disabling
 * - Accessible: `role="group"` with `aria-pressed` on each button
 * - Built on Material UI ButtonGroup for consistent styling
 *
 * @example
 * ```tsx
 * <ToggleButtonGroup
 *   options={[
 *     { value: 'buy', label: 'Buy' },
 *     { value: 'rent', label: 'Rent' },
 *   ]}
 *   value={mode}
 *   onValueChange={setMode}
 *   ariaLabel="Listing type"
 * />
 * ```
 */
export default function ToggleButtonGroup<T extends string | number>({
  options,
  value,
  onValueChange,
  size = 'medium',
  fullWidth = false,
  ariaLabel,
  disabled = false,
  className,
  testId,
}: ToggleButtonGroupProps<T>) {
  const sizeMap = {
    small: { muiSize: 'small' as const, minHeight: '32px', fontSize: '12px', padding: '4px 10px' },
    medium: { muiSize: 'medium' as const, minHeight: '40px', fontSize: '13px', padding: '6px 14px' },
    large: { muiSize: 'large' as const, minHeight: '50px', fontSize: '15px', padding: '10px 18px' },
  };

  const { muiSize, minHeight, fontSize, padding } = sizeMap[size];

  return (
    <ButtonGroup
      className={buildClassName(className)}
      size={muiSize}
      fullWidth={fullWidth}
      disabled={disabled}
      role="group"
      aria-label={ariaLabel}
      data-testid={testId}
      disableElevation
      sx={{
        display: 'inline-flex',
        padding: '2px',
        gap: '2px',
        borderRadius: '120px',
        border: '1px solid var(--themes-colors-border, rgba(0, 0, 0, .12))',
        backgroundColor: 'var(--themes-colors-background, white)',
        boxShadow: 'none',
      }}
    >
      {options.map(option => {
        const isSelected = option.value === value;
        return (
          <Button
            key={String(option.value)}
            type="button"
            disableRipple
            aria-pressed={isSelected}
            disabled={disabled || option.disabled}
            onClick={() => onValueChange(option.value)}
            data-testid={testId ? `${testId}-${String(option.value)}` : undefined}
            sx={{
              flex: fullWidth ? 1 : '0 0 auto',
              minHeight,
              minWidth: 'unset',
              padding,
              fontSize,
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 500,
              textTransform: 'none',
              whiteSpace: 'nowrap',
              border: '0 !important',
              borderRadius: '120px !important',
              transition: 'background-color 0.2s ease, color 0.2s ease',
              // Selected buttons sit on the tenant's `primary-filters` background, which is a
              // saturated/dark brand color on every tenant (blue on Zoocasa, navy on eXp), so the
              // foreground is white to guarantee contrast. Pairing `primary-filters` with white
              // matches the platform's existing selected-filter convention (see control/button-group)
              // and avoids the near-black-on-near-black result on tenants whose `primary-font-color`
              // is dark (e.g. eXp CA/US).
              color: isSelected
                ? 'var(--themes-colors-primary-filters-font-color, #fff)'
                : 'var(--themes-colors-text, rgba(0, 0, 0, .7))',
              backgroundColor: isSelected
                ? 'var(--themes-colors-primary-filters, black)'
                : 'transparent',
              '&:hover': {
                backgroundColor: isSelected
                  ? 'var(--themes-colors-primary-filters, black)'
                  : 'var(--themes-colors-hover-background, rgba(0, 0, 0, .04))',
              },
              '&:focus-visible': {
                outline: '2px solid var(--themes-colors-primary, black)',
                outlineOffset: '2px',
              },
              '&:disabled': {
                opacity: 0.5,
                cursor: 'not-allowed',
              },
            }}
          >
            {option.label}
          </Button>
        );
      })}
    </ButtonGroup>
  );
}
